/**
 * 工单看板。
 * MVP 不做拖拽换列 —— 状态流转有业务规则，拖拽会绕过规则。点卡片进详情推进。
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ClipboardList, PackageCheck, Search, X } from 'lucide-react'
import { api, type CaseListItem, type Me } from '../lib/api'
import {
  ARCH_ZH,
  CASE_STATUS_ZH,
  CASE_TYPE_SHORT,
  date as fmtDate,
  relativeDay,
  rm,
} from '../lib/format'
import { Badge, Button, Card, Chip, EmptyState, Input, Skeleton, cx } from '../components/ui'

const COLUMNS: Array<{ key: string; label: string; statuses: string[] }> = [
  { key: 'active', label: '进行中', statuses: ['DRAFT', 'IN_PROGRESS', 'ON_HOLD'] },
  { key: 'ready', label: '可取件', statuses: ['READY'] },
  { key: 'unpaid', label: '已交付待收', statuses: ['DELIVERED'] },
  { key: 'done', label: '已完成', statuses: ['CLOSED'] },
  // 已取消原本【不属于任何一列】，等于开出去又取消的单从界面上彻底消失了。
  // 单子还在库里，却没有任何地方看得到 —— 这是「查不到单」最主要的原因。
  { key: 'cancelled', label: '已取消', statuses: ['CANCELLED'] },
]

export function CasesPage({ me }: { me: Me }) {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const filter = params.get('filter')
  const [view, setView] = useState<'board' | 'list'>('board')

  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  const [limit, setLimit] = useState(100)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250)
    return () => clearTimeout(t)
  }, [term])

  const q = useQuery({
    queryKey: ['cases', debounced, limit],
    queryFn: () =>
      api.get<{ items: CaseListItem[]; total: number }>(
        `/api/cases?limit=${limit}` + (debounced.length >= 2 ? `&q=${encodeURIComponent(debounced)}` : ''),
      ),
    placeholderData: (p) => p,
  })

  const items = q.data?.items ?? []
  const total = q.data?.total ?? 0
  const searching = debounced.length >= 2

  const filtered = useMemo(() => {
    if (filter === 'overdue') {
      return items.filter(
        (c) =>
          c.promisedDate &&
          new Date(c.promisedDate) < new Date() &&
          ['IN_PROGRESS', 'READY', 'DRAFT'].includes(c.status),
      )
    }
    if (filter === 'ready') return items.filter((c) => c.status === 'READY')
    return items
  }, [items, filter])

  if (q.isLoading) {
    return (
      <div className="grid gap-3 md:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-72" />
        ))}
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-full">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="flex-1 text-[28px] font-bold">
          工单
          {filter === 'overdue' && <span className="ml-2 text-bad">· 逾期</span>}
          {filter === 'ready' && <span className="ml-2 text-brand-700">· 待取件</span>}
        </h1>
        <div className="flex gap-2">
          <Chip active={view === 'board'} onClick={() => setView('board')}>
            看板
          </Chip>
          <Chip active={view === 'list'} onClick={() => setView('list')}>
            列表
          </Chip>
        </div>
      </div>

      <div className="relative mb-4">
        <Search size={20} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-3" />
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="单号 / 顾客姓名 / 电话"
          className="h-13 pl-11 pr-11 text-[17px]"
        />
        {term && (
          <button
            onClick={() => setTerm('')}
            aria-label="清空"
            className="absolute right-2 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full text-ink-3 hover:bg-sunken"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {searching && (
        <p className="tnum mb-3 text-[16px] text-ink-2">
          找到 {total} 张
        </p>
      )}

      {filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon={<ClipboardList size={44} />}
            title={filter ? '这里是空的' : '还没有工单'}
            description={filter ? '没有符合条件的工单，这是好事。' : '点右下角「开单」建第一张。'}
          />
        </Card>
      ) : view === 'list' || filter || searching ? (
        <CaseList items={filtered} onOpen={(id) => navigate(`/cases/${id}`)} />
      ) : (
        // 手机直接用列表 —— 看板横向滚动在小屏找不到东西
        <>
        <div className="md:hidden">
          <CaseList items={filtered} onOpen={(id) => navigate(`/cases/${id}`)} />
        </div>
        <div className="-mx-4 hidden gap-3 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:px-6 md:flex">
          {COLUMNS.map((col) => {
            const colItems = items.filter((c) => col.statuses.includes(c.status))
            return (
              <div key={col.key} className="flex w-72 shrink-0 flex-col gap-2 xl:w-auto xl:flex-1">
                <h2 className="text-[17px] font-semibold text-ink-2">
                  {col.label} <span className="tnum text-ink-3">({colItems.length})</span>
                </h2>
                {colItems.length === 0 ? (
                  <div className="rounded-md border border-dashed border-line-strong px-4 py-6 text-center text-[15px] text-ink-3">
                    暂无
                  </div>
                ) : (
                  colItems.map((c) => (
                    <CaseCard key={c.id} c={c} onClick={() => navigate(`/cases/${c.id}`)} />
                  ))
                )}
              </div>
            )
          })}
        </div>
        </>
      )}

      {/* 原本写死 limit=100，超过一百张之后更早的单就再也翻不到了 */}
      {items.length >= limit && items.length < total && (
        <div className="mt-4 flex justify-center">
          <Button size="lg" loading={q.isFetching} onClick={() => setLimit((n) => n + 100)}>
            显示更早的（还有 {total - items.length} 张）
          </Button>
        </div>
      )}
    </div>
  )
}

function isOverdue(c: CaseListItem): boolean {
  return (
    !!c.promisedDate &&
    new Date(c.promisedDate) < new Date() &&
    ['IN_PROGRESS', 'READY', 'DRAFT'].includes(c.status)
  )
}

function CaseCard({ c, onClick }: { c: CaseListItem; onClick: () => void }) {
  const overdue = isOverdue(c)
  return (
    <button
      onClick={onClick}
      className={cx(
        'relative w-full rounded-md border bg-white px-4 py-3 text-left shadow-xs transition-colors hover:border-line-strong',
        overdue ? 'border-bad-line' : 'border-line',
      )}
    >
      {overdue && (
        <span className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full bg-bad" />
      )}
      <div className="tnum text-[15px] font-semibold text-ink-3">{c.caseNo}</div>
      <div className="truncate text-[17px] font-semibold">{c.customer.fullName}</div>
      <div className="text-[15px] text-ink-2">
        {c.typeLabel}
        {c.arch ? ` ${ARCH_ZH[c.arch]}` : ''}
        {c.currentStep ? ` · ${c.currentStep}` : ''}
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {overdue ? (
          <Badge tone="bad" icon={<AlertTriangle size={13} />}>
            逾期 {relativeDay(c.promisedDate!).replace(' 天前', '')} 天
          </Badge>
        ) : c.status === 'READY' ? (
          <Badge tone="ok" icon={<PackageCheck size={13} />}>
            {c.readyAt ? `做好 ${relativeDay(c.readyAt)}` : '可取件'}
          </Badge>
        ) : c.promisedDate ? (
          <span className="tnum text-[14px] text-ink-3">交期 {fmtDate(c.promisedDate)}</span>
        ) : null}

        {c.hasOutstanding && (
          <Badge tone="warn">
            {c.outstandingAmount === null ? '有欠款' : `欠 ${rm(c.outstandingAmount)}`}
          </Badge>
        )}
      </div>
    </button>
  )
}

function CaseList({ items, onOpen }: { items: CaseListItem[]; onOpen: (id: string) => void }) {
  return (
    <Card bodyClassName="p-0! sm:p-0!">
      <ul>
        {items.map((c) => (
          <li key={c.id}>
            <button
              onClick={() => onOpen(c.id)}
              className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 border-b border-line px-5 py-4 text-left transition-colors last:border-0 hover:bg-sunken sm:px-6"
            >
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-[17px] font-semibold">{c.customer.fullName}</span>
                  <Badge tone={isOverdue(c) ? 'bad' : 'neutral'}>
                    {isOverdue(c) ? '逾期' : CASE_STATUS_ZH[c.status]}
                  </Badge>
                  {c.hasOutstanding && c.outstandingAmount !== null && (
                    <Badge tone="warn">欠 {rm(c.outstandingAmount)}</Badge>
                  )}
                </span>
                <span className="tnum block text-[15px] text-ink-3">
                  {c.caseNo} · {c.typeLabel}
                  {c.arch ? ` ${ARCH_ZH[c.arch]}` : ''}
                  {c.currentStep ? ` · ${c.currentStep}` : ''}
                </span>
              </span>
              <span className="tnum shrink-0 text-right text-[17px]">
                {rm(c.finalAmount)}
                {c.promisedDate && (
                  <span className="block text-[15px] text-ink-3">{fmtDate(c.promisedDate)}</span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  )
}
